/* eslint-disable no-nested-ternary */
import { GraphicalOutputPlaceholder, TrackerContext } from '@datacamp/campus-integration';
import { ExerciseStatus } from '@datacamp/exercise-contract';
import { Reconnector } from '@datacamp/le-shared-components';
import { hooks } from '@datacamp/teach-integration';
import jsCookie from 'js-cookie';
import React, { useCallback, useEffect } from 'react';
import config from '../config';
import { RemoteDesktopManagerContext } from '../containers/RemoteDesktopManager';
import { GuacamoleError } from '../lib/ConnectedVirtualMachine';
import { useTrackVmSessionStatus } from '../lib/hooks/useTrackVmSessionStatus';
import { useMeasureLoadingTime } from '../lib/metrics/useMeasureLoadingTime';
import { sentry } from '../lib/sentry';
import { ConversationalInterface } from './Exercise/ConversationalInterfaceLayout/ConversationalInterface';
import { GuacamoleClient } from './GuacamoleClient';
import { jsx as ___EmotionJSX } from "@emotion/react";
const {
  usePreview
} = hooks;
const usePreviewCookie = _ref => {
  let {
    datacampDomain,
    isPreview
  } = _ref;
  useEffect(() => {
    jsCookie.set('rde-is-preview', String(isPreview), {
      domain: datacampDomain
    });
  }, [isPreview, datacampDomain]);
};
const RemoteDesktop = _ref2 => {
  var _context$session$mess;
  let {
    environment,
    exercise,
    exerciseId,
    maxRetry = 3,
    onCheckExercise,
    onContinue,
    resources
  } = _ref2;
  const context = React.useContext(RemoteDesktopManagerContext);
  const {
    tracker
  } = React.useContext(TrackerContext);
  const [showReconnector, setShowReconnector] = React.useState(false);
  const [retry, setRetry] = React.useState(0);
  const [rootError, setRootError] = React.useState();
  const [guacamoleStatus, setGuacamoleStatus] = React.useState();
  const [errorMessage, setErrorMessage] = React.useState('');
  const {
    incrementSessionDeath,
    incrementSessionDisconnect,
    incrementSessionStart
  } = useTrackVmSessionStatus(exercise.course_id, environment, errorMessage);
  const {
    isPreview
  } = usePreview();
  const datacampDomain = config(environment).DATACAMP_DOMAIN;
  usePreviewCookie({
    datacampDomain,
    isPreview
  });
  const restartSession = useCallback(_ref3 => {
    let {
      forceNew
    } = _ref3;
    if (context.client === undefined) {
      return;
    }
    context.client.initialize({
      forceNew
    });
  }, [context.client]);
  const onGuacamoleError = useCallback(function () {
    var _context$client;
    let forceShowReconnector = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : false;
    let originalError = arguments.length > 1 ? arguments[1] : undefined;
    let _guacamoleStatus = arguments.length > 2 ? arguments[2] : undefined;
    setRootError(originalError);
    setGuacamoleStatus(_guacamoleStatus);
    if (forceShowReconnector) {
      setRetry(maxRetry);
    }
    (_context$client = context.client) === null || _context$client === void 0 || _context$client.disconnect();
  }, [context.client, maxRetry]);
  const getStatusMessage = () => {
    const didUserOpenAnotherTabOrBrowser = context.socket.status === 'duplicate' || (guacamoleStatus === null || guacamoleStatus === void 0 ? void 0 : guacamoleStatus.code) === GuacamoleError.Duplicate;
    if (context.session.status === 'error') {
      return 'session-error';
    }
    if (context.socket.status === 'error') {
      return 'websocket-error';
    }
    if (didUserOpenAnotherTabOrBrowser) {
      return 'websocket-duplicate-error';
    }
    return undefined;
  };
  const tryRestartSession = () => {
    if (retry >= maxRetry) {
      var _getStatusMessage;
      const sentryContext = {
        guacamoleCode: guacamoleStatus === null || guacamoleStatus === void 0 ? void 0 : guacamoleStatus.code,
        guacamoleMessage: guacamoleStatus === null || guacamoleStatus === void 0 ? void 0 : guacamoleStatus.message,
        sessionId: context.sessionId
      };
      sentry.captureError(rootError !== null && rootError !== void 0 ? rootError : new Error('Reconnector shown'), sentryContext);
      const showReconnectorCauseOutsideUser = context.socket.status !== 'duplicate';
      if (showReconnectorCauseOutsideUser) {
        const errorType = getStatusMessage();
        tracker.eventUserResponseSessionFrontendError(errorType);
      }
      setErrorMessage((_getStatusMessage = getStatusMessage()) !== null && _getStatusMessage !== void 0 ? _getStatusMessage : '');
      incrementSessionDeath();
      setShowReconnector(true);
      return;
    }

    // we only want to report the disconnect on the first reconnection attempt
    // the retries happen in the background and if after 3 retries it still fails
    // we report session death instead.
    if (retry === 0) {
      var _getStatusMessage2;
      setErrorMessage((_getStatusMessage2 = getStatusMessage()) !== null && _getStatusMessage2 !== void 0 ? _getStatusMessage2 : '');
      incrementSessionDisconnect();
    }
    restartSession({
      forceNew: false
    });
    setRetry(r => r + 1);
  };
  React.useEffect(() => {
    if (context.exerciseStatus === ExerciseStatus.BROKEN) {
      tryRestartSession();
      return;
    }
    setShowReconnector(false);
    // Don't add the tryRestartSession function as dep to avoid triggering the effect at every render
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [context.exerciseStatus]);
  useMeasureLoadingTime('campus__time_to_vm_session_load', context.session, {
    courseId: exercise.course_id,
    environment,
    technology: 'other'
  });
  const onGuacamoleReady = React.useCallback(() => {
    incrementSessionStart();
    setRetry(0);
  }, [setRetry, incrementSessionStart]);
  const showDebugMessage = isPreview || environment === 'LOCAL';
  const debugMessage = (_context$session$mess = context.session.message) !== null && _context$session$mess !== void 0 ? _context$session$mess : context.socket.message;
  const forceRestartSession = useCallback(() => {
    restartSession({
      forceNew: true
    });
  }, [restartSession]);
  return showReconnector ? ___EmotionJSX(Reconnector, {
    debugMessage: showDebugMessage ? debugMessage : undefined,
    restartSession: forceRestartSession,
    statusMessage: getStatusMessage()
  }) : context.exerciseStatus === ExerciseStatus.BUSY || context.sessionId === null ? ___EmotionJSX(GraphicalOutputPlaceholder, {
    message: context.session.status === 'pending' ? context.session.message : undefined
  }) : ___EmotionJSX(React.Fragment, null, ___EmotionJSX(GuacamoleClient, {
    environment: environment,
    onError: onGuacamoleError,
    onReady: onGuacamoleReady,
    sessionId: context.sessionId,
    zoom: context.zoom
  }), ___EmotionJSX(ConversationalInterface, {
    environment: environment,
    exercise: exercise,
    exerciseId: exerciseId,
    forceRestartSession: forceRestartSession,
    onCheckExercise: onCheckExercise,
    onContinue: onContinue,
    resources: resources
  }));
};
export default RemoteDesktop;